import React, { useMemo, useCallback, useEffect } from 'react';
import { StyleSheet, View, Pressable, Platform, Alert } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useModalStore, useAuthStore, useCreditPacksStore, useToastStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { getCreditPackIapProductId } from '@/hooks/useIosIap';
import PropTypes from 'prop-types';
import InternationalPaymentNotice from '@/components/InternationalPaymentNotice';
import copyText from '@/constants/utils/copy_text';
import {
  trackConversionEvent,
  setCheckoutSourceDimension,
  CONVERSION_ACTION,
  eventName,
} from '@/constants/utils/analyticsEvents';

const DEPLETED_SURFACE = 'credits_depleted';
import useTranslation from '@/hooks/useTranslation';
import PlusOutOfCreditsOptions from '@/components/Credits/PlusOutOfCreditsOptions';
import { formatCreditBalance, wholeCredits } from '@/lib/formatCredits';

const isNative = Platform.OS !== 'web';

const createStyles = (colors, isDark) => StyleSheet.create({
  container: {
    marginBottom: 8,
    maxWidth: '100%',
  },
  card: {
    backgroundColor: colors.solid_color_secondary,
    borderRadius: isNative ? 14 : 16,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: isNative ? 14 : 16,
    paddingTop: isNative ? 14 : 16,
    paddingBottom: isNative ? 10 : 12,
    gap: isNative ? 10 : 12,
  },
  iconContainer: {
    width: isNative ? 34 : 40,
    height: isNative ? 34 : 40,
    borderRadius: isNative ? 17 : 20,
    backgroundColor: colors.accent_color_primary + '15',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    flex: 1,
  },
  title: {
    marginBottom: 2,
  },
  originalMessageContainer: {
    backgroundColor: colors.solid_color_tertiary,
    paddingHorizontal: isNative ? 14 : 16,
    paddingVertical: isNative ? 10 : 12,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  originalMessageLabel: {
    marginBottom: isNative ? 4 : 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  originalMessageLabelLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  copyButton: {
    padding: 4,
    borderRadius: 4,
  },
  copyButtonHovered: {
    backgroundColor: colors.solid_color_primary,
  },
  originalMessageText: {
    lineHeight: isNative ? 20 : 22,
  },
  quickTopUpSection: {
    paddingHorizontal: isNative ? 14 : 16,
    paddingVertical: isNative ? 12 : 14,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
  },
  quickTopUpHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: isNative ? 10 : 12,
  },
  cardDisplay: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  cardIconSmall: {
    width: 32,
    height: 22,
    borderRadius: 4,
    backgroundColor: colors.solid_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickTopUpButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: isNative ? 8 : 10,
    flexWrap: 'wrap',
  },
  moreOptionsLink: {
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  footer: {
    paddingHorizontal: isNative ? 14 : 16,
    paddingVertical: isNative ? 10 : 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  learnMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: isNative ? 0 : 10,
    borderRadius: 6,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  learnMoreButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  footerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  plusOptionsSection: {
    paddingHorizontal: isNative ? 14 : 16,
    paddingVertical: isNative ? 12 : 14,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  // Credit packs section
  creditPacksSection: {
    paddingHorizontal: isNative ? 14 : 16,
    paddingVertical: isNative ? 12 : 14,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
  },
  creditPacksHeader: {
    marginBottom: isNative ? 10 : 12,
  },
  packOptions: {
    flexDirection: 'row',
    gap: isNative ? 6 : 8,
    flexWrap: 'wrap',
  },
  packOption: {
    flex: 1,
    minWidth: isNative ? 80 : 90,
    ...(Platform.OS === 'web' && { maxWidth: 120 }),
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: isNative ? 10 : 10,
    paddingVertical: isNative ? 10 : 12,
    paddingHorizontal: isNative ? 8 : 10,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  packOptionHovered: {
    borderColor: colors.accent_color_primary,
    backgroundColor: colors.accent_color_primary + '10',
  },
  packOptionPopular: {
    borderColor: colors.accent_color_primary + '60',
    backgroundColor: colors.accent_color_primary + '08',
  },
  packCredits: {
    marginBottom: 1,
  },
  packPrice: {
    marginTop: isNative ? 2 : 4,
  },
  popularBadge: {
    position: 'absolute',
    top: -8,
    right: -4,
    backgroundColor: colors.accent_color_primary,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  bestValueBadge: {
    position: 'absolute',
    top: -8,
    right: -4,
    backgroundColor: '#22c55e',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  seeAllLink: {
    marginTop: isNative ? 10 : 12,
    alignItems: 'center',
  },
  // Plus section
  plusSection: {
    paddingHorizontal: isNative ? 14 : 16,
    paddingVertical: isNative ? 12 : 14,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  plusDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: isNative ? 10 : 14,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border_color_secondary,
  },
  plusHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: isNative ? 8 : 12,
  },
  benefitsList: {
    marginBottom: isNative ? 10 : 14,
    gap: isNative ? 6 : 8,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: isNative ? 8 : 10,
  },
  benefitIconContainer: {
    width: isNative ? 22 : 24,
    height: isNative ? 22 : 24,
    borderRadius: isNative ? 11 : 12,
    backgroundColor: colors.accent_color_primary + '15',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

function CreditsDepletedMessage({ message }) {
  const { t } = useTranslation();
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { openModal } = useModalStore(state => state.actions);
  const { openCreditPacksCheckout, usesWhopCheckout, iap } = useGabPurchase();
  
  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable) ?? 0;
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  
  // Check if user has stored payment method for quick top-up (US only)
  const hasPaymentMethod = useAuthStore(state => state.user?.valmar?.hasPaymentMethod);
  const maskedCardData = useAuthStore(state => state.user?.valmar?.maskedCardData);
  const canQuickPurchase = (Platform.OS === 'web' || Platform.OS === 'macos') && !usesWhopCheckout && hasPaymentMethod && maskedCardData?.maskedNumber;
  
  // Get credit packs
  const creditPacks = useCreditPacksStore(state => state.creditPacks);
  const fetchCreditPacks = useCreditPacksStore(state => state.fetchCreditPacks);
  
  useEffect(() => {
    trackConversionEvent(
      CONVERSION_ACTION.PAYWALL_SHOWN,
      eventName(DEPLETED_SURFACE, isPlus ? 'plus' : 'free'),
      totalAvailableCredits,
    );
    setCheckoutSourceDimension(DEPLETED_SURFACE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch credit packs on mount if not loaded
  useEffect(() => {
    if (!creditPacks?.length) {
      fetchCreditPacks();
    }
  }, [creditPacks, fetchCreditPacks]);
  
  // Get top 3 packs for display
  const displayPacks = useMemo(() => {
    if (!creditPacks?.length) return [];
    return creditPacks.slice(0, 3);
  }, [creditPacks]);

  const getPackPrice = useCallback((pack) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      const productId = getCreditPackIapProductId(pack.credits);
      const product = productId ? iap?.getProduct?.(productId) : null;
      if (product?.localizedPrice) return product.localizedPrice;
      if (product?.price != null) return `$${parseFloat(product.price).toFixed(2)}`;
    }
    return `$${pack.price?.toFixed(0)}`;
  }, [iap]);

  // Get card display info
  const getCardTypeName = (cardType) => {
    const types = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'Amex',
      discover: 'Discover',
    };
    return types[cardType?.toLowerCase()] || cardType || 'Card';
  };
  const cardLastFour = maskedCardData?.maskedNumber?.slice(-4) || '****';

  const handleBuyCredits = useCallback(() => {
    trackConversionEvent(CONVERSION_ACTION.CREDITS_DEPLETED_CLICKED, eventName(DEPLETED_SURFACE, 'buy_credits'));
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  }, [openModal]);

  const handleUpgrade = useCallback(() => {
    trackConversionEvent(CONVERSION_ACTION.CREDITS_DEPLETED_CLICKED, eventName(DEPLETED_SURFACE, 'upgrade'));
    openModal('PricingModal', {
      title: t('messages.creditsDepleted.upgradeTitle'),
      subtitle: t('messages.creditsDepleted.upgradeSubtitle')
    });
  }, [openModal]);

  // Handle pack selection - different flow based on payment method (US only)
  const handleSelectPack = useCallback(async (pack) => {
    const packId = pack?.id ?? pack;
    const credits = typeof pack === 'object' ? pack?.credits : undefined;
    trackConversionEvent(CONVERSION_ACTION.CREDITS_DEPLETED_CLICKED, eventName(DEPLETED_SURFACE, 'pack'), credits);
    try {
      if (canQuickPurchase) {
        openModal('QuickPurchaseConfirmModal', { creditPackId: packId });
      } else {
        await openCreditPacksCheckout({ creditPackId: packId, credits });
      }
    } catch (error) {
      console.error('[CreditsDepletedMessage] Purchase error:', error);
      if (Platform.OS !== 'web') {
        Alert.alert(t('messages.creditsDepleted.purchaseError'), error?.message || t('messages.creditsDepleted.purchaseErrorMsg'));
      }
    }
  }, [canQuickPurchase, openModal, openCreditPacksCheckout]);

  const handleLearnMore = useCallback(() => {
    openModal('CreditsInfoModal');
  }, [openModal]);

  // Get the original message content (strip any HTML tags if present)
  const originalMessage = useMemo(() => {
    if (!message?.content) return '';
    return message.content.replace(/<[^>]*>?/gm, '').trim();
  }, [message?.content]);

  const { addToast } = useToastStore(state => state.actions);
  
  const handleCopyMessage = useCallback(async () => {
    if (!originalMessage) return;
    try {
      await copyText(originalMessage);
      addToast(t('messages.copied'), 'success');
    } catch (error) {
      console.error('Failed to copy message:', error);
      addToast(t('messages.copyFailed'), 'error');
    }
  }, [originalMessage, addToast]);

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Icon 
              name="bolt" 
              size={isNative ? 16 : 20}
              color={colors.accent_color_primary}
              solid
            />
          </View>
          <View style={styles.headerText}>
            <Text size="n" weight="semibold" color="primary" style={styles.title}>
              {totalAvailableCredits === 0
                ? (isPlus ? t('plusOutOfCredits.inlineTitle') : t('messages.creditsDepleted.outOfCredits'))
                : t('messages.creditsDepleted.notEnoughCredits')
              }
            </Text>
            <Text size="s" color="secondary">
              {totalAvailableCredits === 0
                ? (isPlus ? t('plusOutOfCredits.inlineSubtitle') : t('messages.creditsDepleted.balanceEmpty'))
                : t('messages.creditsDepleted.needMoreCredits').replace('%COUNT%', formatCreditBalance(totalAvailableCredits)).replace('%S%', wholeCredits(totalAvailableCredits) !== 1 ? 's' : '')
              }
            </Text>
          </View>
        </View>

        {/* Original message preview */}
        {originalMessage && (
          <View style={styles.originalMessageContainer}>
            <View style={styles.originalMessageLabel}>
              <View style={styles.originalMessageLabelLeft}>
                <Icon 
                  name="message" 
                  size={12}
                  color={colors.text_color_tertiary}
                />
                <Text size="xs" color="tertiary" weight="medium">
                  {t('messages.creditsDepleted.yourMessage')}
                </Text>
              </View>
              <Pressable
                onPress={handleCopyMessage}
                accessibilityRole="button"
                accessibilityLabel="Copy message to clipboard"
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                style={({ hovered }) => [
                  styles.copyButton,
                  hovered && styles.copyButtonHovered,
                ]}
              >
                <Icon 
                  name="clipboard" 
                  size={14}
                  color={colors.text_color_tertiary}
                />
              </Pressable>
            </View>
            <Text 
              size="n" 
              color="secondary" 
              style={styles.originalMessageText}
              numberOfLines={3}
            >
              {originalMessage}
            </Text>
          </View>
        )}

        {/* International Payment Notice */}
        <View style={{ paddingHorizontal: isNative ? 14 : 16, paddingTop: isNative ? 8 : 12 }}>
          <InternationalPaymentNotice />
        </View>

        {/* Plus / Plus Premium: Arya for free, buy credits, upgrade to Premium, refresh date */}
        {isPlus && (
          <View style={styles.plusOptionsSection}>
            <PlusOutOfCreditsOptions source="chat_inline" />
          </View>
        )}

        {/* Credit Packs Section - free users */}
        {!isPlus && displayPacks.length > 0 && (
          <View style={styles.creditPacksSection}>
            <View style={styles.creditPacksHeader}>
              <Text size="s" weight="semibold" color="primary">
                {t('messages.creditsDepleted.chooseAPack')}
              </Text>
              {canQuickPurchase && (
                <View style={[styles.cardDisplay, { marginTop: 6 }]}>
                  <View style={styles.cardIconSmall}>
                    <Icon name="credit-card" size={12} color={colors.text_color_primary} />
                  </View>
                  <Text size="xs" color="secondary">
                    {getCardTypeName(maskedCardData?.cardType)} .... {cardLastFour}
                  </Text>
                </View>
              )}
            </View>
            <View style={styles.packOptions}>
              {displayPacks.map((pack) => (
                <Pressable
                  key={pack.id}
                  style={({ hovered }) => [
                    styles.packOption,
                    pack.flags?.isPopular && styles.packOptionPopular,
                    hovered && styles.packOptionHovered,
                  ]}
                  onPress={() => handleSelectPack(pack)}
                >
                  {pack.flags?.isPopular && (
                    <View style={styles.popularBadge}>
                      <Text size="xxs" weight="bold">{t('messages.creditsDepleted.popular')}</Text>
                    </View>
                  )}
                  {pack.flags?.isBestValue && !pack.flags?.isPopular && (
                    <View style={styles.bestValueBadge}>
                      <Text size="xxs" color="white" weight="bold">{t('messages.creditsDepleted.bestValue')}</Text>
                    </View>
                  )}
                  <Text size={isNative ? "n" : "l"} weight="bold" color="primary" style={styles.packCredits}>
                    {pack.credits?.toLocaleString()}
                  </Text>
                  <Text size="xs" color="secondary">{t('messages.creditsDepleted.credits')}</Text>
                  <Text size={isNative ? "s" : "n"} weight="semibold" color="primary" style={styles.packPrice}>
                    {getPackPrice(pack)}
                  </Text>
                </Pressable>
              ))}
            </View>
            <Pressable onPress={handleBuyCredits} style={styles.seeAllLink}>
              <Text size="s" color="secondary" style={{ textDecorationLine: 'underline' }}>
                {t('messages.creditsDepleted.seeAllOptions')}
              </Text>
            </Pressable>
          </View>
        )}

        {/* Plus Benefits Section - shown only for free users */}
        {!isPlus && (
          <View style={styles.plusSection}>
            <View style={styles.plusDivider}>
              <View style={styles.dividerLine} />
              <Text size="xs" color="tertiary" weight="medium">{t('messages.creditsDepleted.or')}</Text>
              <View style={styles.dividerLine} />
            </View>
            <View style={styles.plusHeader}>
              <Icon name="sparkles" size={isNative ? 14 : 16} color={colors.plus_color || colors.accent_color_primary} solid />
              <Text size="n" weight="semibold" color="primary">{t('messages.creditsDepleted.upgradePlus')}</Text>
            </View>
            <View style={styles.benefitsList}>
              <View style={styles.benefitItem}>
                <View style={styles.benefitIconContainer}>
                  <Icon name="infinity" size={isNative ? 10 : 12} color={colors.text_color_primary} />
                </View>
                <Text size="s" color="secondary">{t('messages.creditsDepleted.freeDefaultMessages')}</Text>
              </View>
              <View style={styles.benefitItem}>
                <View style={styles.benefitIconContainer}>
                  <Icon name="coins" size={isNative ? 10 : 12} color={colors.text_color_primary} />
                </View>
                <Text size="s" color="secondary">{t('messages.creditsDepleted.monthlyCredits')}</Text>
              </View>
              <View style={styles.benefitItem}>
                <View style={styles.benefitIconContainer}>
                  <Icon name="brain" size={isNative ? 10 : 12} color={colors.text_color_primary} />
                </View>
                <Text size="s" color="secondary">{t('messages.creditsDepleted.memoryPersonalization')}</Text>
              </View>
            </View>
            <Button
              variant="plus"
              text={t('messages.creditsDepleted.upgradePlus')}
              size="small"
              prependIcon="sparkles"
              onPress={handleUpgrade}
              isFullWidth
            />
          </View>
        )}

        {/* Footer with What are credits link */}
        <View style={styles.footer}>
          <Pressable
            onPress={handleLearnMore}
            accessibilityRole="button"
            accessibilityLabel="Learn what credits are"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={({ hovered, pressed }) => [
              styles.learnMoreButton,
              hovered && styles.learnMoreButtonHovered,
              pressed && { opacity: 0.7 }
            ]}
          >
            <Icon 
              name="circle-question" 
              size={13} 
              color={colors.text_color_tertiary} 
            />
            <Text size="s" color="tertiary">
              {t('messages.creditsDepleted.whatAreCredits')}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

CreditsDepletedMessage.propTypes = {
  message: PropTypes.shape({
    content: PropTypes.string,
    buttons: PropTypes.array,
  }),
};

CreditsDepletedMessage.displayName = 'CreditsDepletedMessage';

export default React.memo(CreditsDepletedMessage);

